import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  BookOpen, ArrowLeft, ChevronRight, Loader2,
  History, Globe, Scale, TrendingUp, Beaker
} from 'lucide-react';

import { getApiBaseUrl } from '../services/api';
const API_URL = getApiBaseUrl();

const SUBJECT_ICONS = {
  history: History,
  geography: Globe,
  polity: Scale,
  economics: TrendingUp,
  science: Beaker
};

const FoundationSubjectPage = () => {
  const { subjectId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [subject, setSubject] = useState(null);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }
    fetchBooks();
  }, [user, token, subjectId, navigate]);

  const fetchBooks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/foundation/subjects/${subjectId}/books`);
      if (!res.ok) throw new Error('Failed to fetch');
      
      const data = await res.json();
      setSubject(data.subject);
      setBooks(data.books || []);
      
    } catch (error) {
      console.error('Error fetching books:', error);
      toast.error('Failed to load subject');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!subject) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30 flex items-center justify-center">
        <p className="text-slate-500">Subject not found</p>
      </div>
    );
  }

  const SubjectIcon = SUBJECT_ICONS[subject.id] || BookOpen;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30" data-testid="foundation-subject-page">
      
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center gap-3 mb-4">
            <Link 
              to="/foundation" 
              className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div 
                className="p-3 rounded-xl bg-white/10"
              >
                <SubjectIcon className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold font-heading">
                  {subject.name}
                </h1>
                <p className="text-emerald-200 text-sm">
                  {books.length} books • Class 6-12 NCERT
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Books List */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-700">Select a Book</h2>
          
          {books.map((book) => (
            <Link 
              key={book.id} 
              to={`/foundation/${subjectId}/book/${book.id}`}
              data-testid={`foundation-book-${book.id}`}
            >
              <Card className="bg-white border-slate-200 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer group">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-xl font-bold"
                        style={{ backgroundColor: `${subject.color}15`, color: subject.color }}
                      >
                        {book.class_num}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 group-hover:text-emerald-600 transition-colors">
                          {book.title}
                        </h3>
                        <p className="text-slate-500 text-sm">
                          Class {book.class_num} • {book.chapters_count} chapters
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FoundationSubjectPage;
